import { Component, effect, inject, signal, OnDestroy } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { createDynamicFilterState, DynamicFilterConfig, DynamicFilterValues } from '@card-software/library';
import { AuthService } from '@card-software/nexus-utilities';
import { finalize, Subject } from 'rxjs';
import { TablePageEvent } from 'primeng/table';
import { ProductDevelopmentAdminGroup, ProductDevelopmentViewerGroup } from '../../../models/authorization/enum/user-group.enum';
import { Process, ProcessSearch } from '../../../models/engineering/process';
import { ProcessProxyService } from '../../../proxies/item/process.proxy.service';

@Component({
  selector: 'app-processes',
  templateUrl: './processes.component.html',
  styleUrl: './processes.component.scss',
})
export class ProcessesComponent implements OnDestroy {
  private router = inject(Router);
  private translateService = inject(TranslateService);
  private processProxyService = inject(ProcessProxyService);

  private destroy$ = new Subject<void>();
  isLoading = signal(0);
  filterConfig = signal<DynamicFilterConfig | null>(null);
  readonly filterState = createDynamicFilterState({ storageKey: 'processes-filters', config: this.filterConfig });
  showFilters = signal<boolean>(true);

  listProcesses = signal<Process[]>([]);
  firstPagination = signal<number>(0);

  translateFilter = effect(
    () => {
      if (this.translateService.currentLang) {
        this.tryInitializeFilterConfig();
      }
    },
    { allowSignalWrites: true },
  );

  constructor() {
    this.filterState.applied$.pipe(takeUntilDestroyed()).subscribe((filters) => this.searchWithFilters(filters));
  }

  getRowData(rowData: unknown): Process {
    return rowData as Process;
  }

  canEdit() {
    // if Admin
    if (AuthService.hasRole(ProductDevelopmentAdminGroup.ProcessesAdmin)) {
      return true;
    }
    // if Viewer
    if (AuthService.hasRole(ProductDevelopmentViewerGroup.ProcessesViewer)) {
      return false;
    }
    // Default
    return false;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private startLoading(): void {
    this.isLoading.update((val) => val + 1);
  }

  private stopLoading(): void {
    this.isLoading.update((val) => val - 1);
  }

  private tryInitializeFilterConfig(): void {
    if (this.translateService.currentLang) {
      this.initializeFilterConfig();
    }
  }

  private initializeFilterConfig(): void {
    const translate = (key: string, fallback: string) => {
      const translation = this.translateService.instant(key);
      return translation === key ? fallback : translation;
    };

    const config: DynamicFilterConfig = {
      persistInUrl: true,
      persistVisibility: true,
      filters: [
        {
          type: 'text',
          formControlName: 'processId',
          label: translate('LabDip_Process_ID', 'Process Id'),
          placeholder: translate('Enter_process_id', 'Enter process id'),
          defaultVisible: true,
        },
        {
          type: 'text',
          formControlName: 'processName',
          label: translate('Process_Name', 'Process Name'),
          placeholder: translate('Enter_process_name', 'Enter process name'),
          defaultVisible: true,
        },
        /* {
          type: 'checkbox',
          formControlName: 'showInFabricGeneralProcessRoute',
          label: translate('Process_Show_Route', 'Show In Fabric Route'),
          defaultVisible: true,
        }, */
      ],
    };

    this.filterConfig.set(config);
  }

  onSearch(searchCriteria: ProcessSearch) {
    this.startLoading();
    this.processProxyService
      .searchProcesses(searchCriteria)
      .pipe(finalize(() => this.stopLoading()))
      .subscribe({
        next: (processes) => {
          this.listProcesses.set(processes.data || []);

          this.onPageChange();
        },
        error: () => {
          this.listProcesses.set([]);
        },
      });
  }

  onPageChange(event?: TablePageEvent) {
    this.firstPagination.set(event?.first || 0);
  }

  private searchWithFilters(filters: DynamicFilterValues): void {
    const searchCriteria: ProcessSearch = {
      processId: filters['processId'] as number | undefined,
      processName: typeof filters['processName'] === 'string' ? filters['processName'].trim() : undefined,
      showInFabricGeneralProcessRoute: filters['showInFabricGeneralProcessRoute'] as boolean | undefined,
    };

    this.onSearch(searchCriteria);
  }

  goToDetails(id: number | string) {
    const queryParams = id ? { id } : {};
    this.router.navigate(['/process-details'], { queryParams });
  }
}
